/**
 * Hint scheduling and display.
 * Manages the timer that triggers hint animations after a delay, fetches the hint bundle from
 * GET /api/games/{gameId}/hint at the moment the delay elapses (the bundle no longer rides state —
 * game-server-api-hint-resource design), applies the hint-float class to the recommended UI
 * element, and renders the hint reason text with inline visual marks. The card-play/discard
 * directive arrives as a base-32 `cards` field decoded via serde (resolveHintTargets); the
 * action/game-type directive still rides the legacy `hint` word array. Reason-arg card refs
 * (isCardCode/parseCardCode below) stay legacy codes by design — i18n display tokens, not the
 * wire directive (game-server-api-hint-cards-serde-form design).
 *
 * The float speaks three kinds of inline mark, from two sources. A reason ARG that is a card code
 * or a game-type wire token becomes a card ref or a game-type ref — the server chose the value, the
 * copy only placed it. A `{icon:<token>}` MARKER in the copy itself becomes a concept icon: the
 * concept is fixed by the reason key, so the marker belongs in the message bundle rather than in a
 * server arg (hint-copy-words-with-existing-iconography). `<token>` is an `ICON_BUILDERS` key —
 * the same vocabulary the glossary's inline term links speak, so a chalice on the float and a
 * chalice beside a `{t:grand|…}` link are the same mark. All three ride one self-carrying sentinel
 * (`\x00VIS:<kind>:<value>\x00`) through the resolved string, so widening to a fourth kind is one
 * arm in appendHintPart rather than a side table to keep in step.
 *
 * Two rules govern WHEN the float appears and how long it lives, both mode-independent
 * (learning-mode-hints-flicker-and-fire-before-settle):
 *  - The hint clock starts when the scene is at rest, not at the phase's resting commit.
 *    scheduleHint awaits animate.js#whenSceneSettled — dispatch tail released, no live GSAP
 *    motion — before starting its HINT_DELAYS timer, so a zero delay means "the moment the scene
 *    settles" rather than "over the flying cards". HINT_DELAYS is now purely the pedagogical
 *    think-first wait; the structural wait is the gate. The hint button's click path is an
 *    explicit request and stays immediate.
 *  - The float is a companion of the PROMPT, not of the timer. It survives state changes while
 *    the same prompt is up and is reconciled in place when the fresh bundle lands (showHintReason:
 *    same content → the node is left alone, changed content → rebuilt with the `.fresh` fade).
 *    The removal seams are explicit and named at their sites: scheduleHint's no-hint returns and
 *    its prompt-change check, applyHint's empty bundle, showReasonIfPresent's missing reason key,
 *    and the settings-flip seam's exported removeHintReason. clearHintTimer removes nothing.
 *
 * A marker's word stays beside its mark — the copy says "Grand", "Skat", "Augen" and shows the
 * icon next to it. The one icon-only mark is the game-type ARG (`createInlineGameTypeRef`, the bid
 * litany's "× ♠ (11)"), where the pip IS the suit's name and a word beside it would say it twice.
 * All three kinds are framed to their ink by frameMarksToInk below and sized by one CSS rule, so a
 * mark renders identically whichever way it arrived. `.hint-suit-only`, worn by the concept icons
 * and the game-type ref, marks the narrower fact that NO RANK LETTER follows — an icon-only ref as
 * against a card ref's pip+letter pair. Since ink-frame-inline-card-refs no stylesheet keys on it;
 * the hint suites read it to tell the two shapes apart.
 */
import { HINT_DELAYS } from "./constants.js";
import {
  getHintTimer,
  setHintTimer,
  getApp,
  getDiscardSelection,
  getBidAnimatedCount,
} from "./state.js";
import { getSessionConfig } from "./session-config.js";
import { getGame } from "./api.js";
import { t } from "./i18n.js";
import { createSuitPipSvg, rankDisplay } from "./cards/cards.js";
import { createGameTypeIcon } from "./icons/game-type-icons.js";
import { ICON_BUILDERS } from "./icons/icon-builders.js";
import { createPateeChevron } from "./icons/prompt-icons.js";
import { frameToInk } from "./icons/ink-frame.js";
import { suitColor, isViewerTurn } from "./utils.js";
import { gsap } from "./vendor/gsap.js";
import { shouldAnimateReveal, whenSceneSettled } from "./animate.js";
import {
  decodeSequence,
  SUIT_NAMES,
  RANK_NAMES,
} from "./serde/card-ordinal.js";

// Bumped by clearHintTimer() (which fires on every mutation/render) — the staleness guard a
// fetch captures before its GET /api/games/{gameId}/hint round-trip and re-checks on resolve.
// If the count has moved on, the state advanced during the round-trip: the server recomputed
// for the now-current position, but the prompt the fetch was for is gone, so the stale bundle
// is dropped.
let hintGeneration = 0;

/**
 * Returns true when learning mode is active (instant hints, zero delay).
 * URL ?instantHints=true|false overrides localStorage.instantHints.
 */
export function isLearningMode() {
  const urlFlag = new URLSearchParams(location.search).get("instantHints");
  if (urlFlag === "true") return true;
  if (urlFlag === "false") return false;
  return localStorage.getItem("instantHints") === "true";
}

// Clears the pending reveal and invalidates whatever is in flight for it — exactly what the name
// says, and no more. It deliberately does NOT remove the reason float: the float is a companion of
// the prompt, not of the timer (module header), so the next scheduleHint decides its fate at the
// next resting commit. Removing it here is what made every bid click tear the float down and
// re-attach identical text ~1s later.
export function clearHintTimer() {
  if (getHintTimer()) {
    clearTimeout(getHintTimer());
    setHintTimer(null);
  }
  hintGeneration++;
}

/**
 * Fetches the current hint bundle and applies it — the async round-trip both scheduleHint's
 * timer-fire and hint-button's click funnel through (shared so the generation-staleness
 * discipline lives in exactly one place). Feeds the fetched bundle's hint fields alongside the
 * caller's state for pendingAction/roundNumber/etc — simplest as a merged object since applyHint
 * reads both. Resolves to whether the hint was actually applied: false when a fetch failure skips
 * it (a missed hint must never block the game) or the generation moved on mid-flight (a stale
 * drop). The auto-dim companion keys its served mark on that, so a reveal that outlived its prompt
 * never dims the next one.
 */
export function requestHint(state) {
  const generation = hintGeneration;
  return getGame("hint")
    .then((bundle) => {
      if (generation !== hintGeneration) return false; // the prompt this was for is gone
      applyHint({ ...state, ...bundle });
      return true;
    })
    .catch((e) => {
      console.warn("[hints] hint fetch failed, skipping:", e.message);
      return false;
    });
}

/**
 * The think-time this state's hint should wait out, or null when no hint belongs to this state at
 * all. Null is also the float's removal signal — see scheduleHint.
 */
function hintDelayFor(state) {
  if (!getSessionConfig().hintsEnabled || !state.pendingAction) return null;
  // AWAIT_AI_CARD's delay is 0 by design (auto-play/spectator commentary, PR #78) — but that
  // resting state is also where every human card play settles between AI turns during normal
  // (non-auto-play) play, including fast/queued play. Scheduling there fetched GET
  // /api/games/{gameId}/hint on every human play; HintService#computeHint dispatches on the
  // server's *current* pendingAction,
  // not the one this fetch was meant for, so a response that lands after the server has since
  // advanced past this rest carries the human's next real prompt's hint (e.g. a genuine
  // cardPlayHint recommendation) — flashed under the stale AWAIT_AI_CARD reason text before the
  // next mutation cleared it. Gate here, not in applyHint, so normal play skips the timer AND the
  // fetch (and the SmartAi work behind it) — not just the display.
  if (state.pendingAction === "AWAIT_AI_CARD" && !getSessionConfig().autoPlay)
    return null;
  // Multiplayer: every other pendingAction reaching here arms an input-affordance hint (the
  // AI-commentary case is fully handled above) — off-turn, don't schedule/reveal it
  // (debug-first-multiplayer-issues). autoPlay's AWAIT_AI_CARD commentary has no spectated
  // seat, so isViewerTurn is always false there — deliberately exempted from this gate.
  if (state.pendingAction !== "AWAIT_AI_CARD" && !isViewerTurn(state))
    return null;
  let baseDelay = HINT_DELAYS[state.pendingAction];
  if (baseDelay == null) return null;
  // First bid prompt: give the player extra time to parse their hand
  const isBidAction =
    state.pendingAction === "AWAIT_BID_RESPONSE" ||
    state.pendingAction === "AWAIT_FIRST_REFUSAL";
  if (isBidAction && getBidAnimatedCount() === 0) baseDelay *= 2;
  return isLearningMode() ? 0 : baseDelay;
}

/**
 * Arms the reveal for `state`, or takes the float down when no hint belongs to it. Returns a
 * promise that resolves once the timer is armed (or the schedule was dropped), so a test can await
 * the settle wait without a test-only parameter.
 *
 * The two waits are separate concerns: the scene-settled gate is structural (never hint over
 * flying cards) and every mode keeps it; HINT_DELAYS is pedagogical (let the player think first)
 * and learning mode drops it to zero. Awaiting the gate BEFORE starting the timer is what makes
 * learning mode "the moment the scene settles" instead of "mid-choreography".
 */
export function scheduleHint(state) {
  clearHintTimer();
  const delay = hintDelayFor(state);
  if (delay == null) {
    // No hint belongs to this state — hints off, a different prompt, an off-turn seat. The float
    // is the prompt's companion, so it leaves with it: this is what makes a card-play reason
    // vanish the moment the card is played and the rest goes to AWAIT_AI_CARD.
    removeHintReason();
    return Promise.resolve();
  }
  removeReasonForOtherPrompt(state.pendingAction);
  // Captured AFTER clearHintTimer's bump, the same discipline requestHint applies to its own
  // round-trip: a clearHintTimer during the settle wait means a newer schedule (or a click) owns
  // the prompt now, so this resumption drops itself. Silently — removing the float is the
  // superseding schedule's decision, not this dead one's.
  const generation = hintGeneration;
  return whenSceneSettled().then(() => {
    if (generation !== hintGeneration) return;
    setHintTimer(setTimeout(() => revealHint(state), delay));
  });
}

/** The reveal at the end of the think-time. The round-trip happens here, not speculatively at
 *  schedule time — the reveal lands at delay + one rtt. why the conditional mark: `served` records
 *  a reveal that actually applied (`applied === true`), mirroring the old synchronous mark. A
 *  stale drop or a failed fetch must not dim the button — the next prompt reuses the same
 *  persistent node, and with hints off that button is the only path left to a hint. */
function revealHint(state) {
  requestHint(state).then((applied) => {
    if (!applied) return;
    const hintBtn = document.querySelector(".hint-button");
    if (hintBtn) hintBtn.dataset.served = "true";
  });
}

/** The float belongs to the prompt it was fetched for — rule 2's "same `pendingAction`, still the
 *  viewer's turn" (the off-turn half is already `hintDelayFor`'s null return). A NEW prompt takes
 *  it down here, at the commit, rather than at the fresh bundle's landing: the bundle lands only
 *  after the settle wait plus the whole `HINT_DELAYS` think-time, so waiting for it would leave
 *  the previous decision's recommendation standing for 6-16s under a prompt it does not answer
 *  (the discard -> game-type hop is the plain case: both hintable, both on the mutation path, no
 *  render to sweep the float). Showing nothing beats showing the wrong thing. */
function removeReasonForOtherPrompt(pendingAction) {
  const el = document.querySelector(".hint-reason");
  if (el != null && el.dataset.promptAction !== pendingAction) el.remove();
}

/** The directive's card codes: decoded from the base-32 `cards` field when the phase is a card
 *  directive, else the legacy `hint` word array — the two are mutually exclusive per phase, the
 *  server populates exactly one. */
function resolveHintTargets(state) {
  return state.cards != null
    ? decodeSequence(state.cards).map((v) => v.code)
    : (state.hint ?? []);
}

export function applyHint(state) {
  const hints = resolveHintTargets(state);
  // An empty bundle is a removal seam, not a no-op: whatever float is up belongs to a prompt this
  // bundle has nothing to say about (the companion-of-the-prompt rule).
  if (hints.length === 0) {
    removeHintReason();
    return;
  }

  try {
    const appEl = getApp();
    // why: the render pipeline wipes innerHTML and rebuilds, then re-runs
    // hint logic — without this gate, the ping and hint-reason fade-in
    // re-fire on every resize even when the underlying hint is unchanged.
    // Round number is part of the key so a new round restores freshness
    // even when the same hint repeats across rounds.
    const hintKey = `${state.roundNumber}|${state.pendingAction}|${hints.join(",")}`;
    const isFreshHint = appEl?.dataset.lastHintKey !== hintKey;
    if (appEl) appEl.dataset.lastHintKey = hintKey;
    // why: the reason is the load-bearing hint output; the highlight/ping is the flourish.
    // Render the reason BEFORE the applier so a throw in the GSAP ping can't suppress it
    // (js/CLAUDE.md: a failed visual flourish must never block the game). Reason-first makes
    // that independence structural rather than relying on the shared catch below.
    showReasonIfPresent(state);
    // Auto-play commentary (AWAIT_AI_CARD) gets the reason and nothing else — the card is
    // already played and visible, so the reason text IS the hint. That needs no branch here:
    // HINT_APPLIERS deliberately carries no AWAIT_AI_CARD entry, which is the same fact
    // hint-button.js#shouldShowHintButton reads from the other side.
    HINT_APPLIERS[state.pendingAction]?.(appEl, hints, isFreshHint);
  } catch (e) {
    console.warn("[hints] applyHint failed, skipping hint display:", e.message);
  }
}

export const HINT_APPLIERS = {
  AWAIT_CARD_PLAY: applyCardPlayHint,
  AWAIT_BID_RESPONSE: applyBidActionHint,
  AWAIT_FIRST_REFUSAL: applyBidActionHint,
  AWAIT_HAND_DECISION: applyHandDecisionHint,
  AWAIT_GAME_TYPE: applyGameTypeHint,
  AWAIT_DISCARD: applyDiscardHint,
};

function applyCardPlayHint(appEl, hints, isFreshHint) {
  // Steady highlight is the CSS .hint-float class; the entrance flourish is the finite GSAP
  // "ping" (pingHintCard), the gesture this phase shares with discard (floatHandCard).
  const cardEl = appEl.querySelector(`.hand .card[data-code="${hints[0]}"]`);
  if (!cardEl) return;
  cardEl.classList.add("hint-float"); // settled static highlight (DESIGN principle 11)
  if (isFreshHint) pingHintCard(cardEl);
}

// One-shot GSAP ping: the "Ring pop" — the card's outer ring expands outward once like a
// radar/sonar ping, with a touch of brightness, then settles back into the static
// .hint-float highlight. DESIGN principle 11's canonical Light Ping shape, finite (no
// perpetual motion). Animates the --hint-ring / --hint-ping CSS vars (box-shadow spread +
// filter), never a transform, so it can't fight the hand-fan positioning transform (the
// existing .hint-float "lift via light, not physics" rationale). Lighting the ring rather
// than the face draws the eye to the card's outline, where you look to identify it.
//
// The one hint gesture for every hand card: trick-play (applyCardPlayHint) and discard
// (floatHandCard) both ping, so the "here's your suggested card" affordance reads the same
// in both phases. Icon-tile hints (bid/hand/game-type) keep the static .hint-float glow only.
//
// Sibling-primitive discipline:
//  - START GATE: skip if the node is already in a mutation tween (gsap.isTweening),
//    node-level — never the dispatch lock — so a hint on one card is free while a
//    mutation animates another.
//  - CANCELLED BY MUTATIONS: a mutation that reshapes this node calls gsap.killTweensOf
//    (the releaseHintPing seam, see trick-mutations.js#canonicalizeToTrickRest and the
//    re-fan site in #playHumanCard), so the mutation is authoritative and the ping yields.
//    The discard path needs no parallel seam. The trick seam exists because the played card
//    reparents into a trick slot and FLIPs, where a live tween or frozen --hint-ring/--hint-ping
//    would ride onto the moving node; no discard path moves a hinted node like that. The ping
//    self-clears on complete (clearProps), only .hint-float reads the vars, and the
//    dispatch/render affordance projection sheds .hint-float (render-hand.js#applyHandAffordances)
//    — so any residue is inert, never orphaned. (A discard card selected mid-ping keeps
//    .hint-float but gains .selected, whose box-shadow overrides the ring; the ping finishes
//    harmlessly.)
function pingHintCard(cardEl) {
  // Reduced-motion / no-GSAP: skip the ping — the static .hint-float highlight is the
  // motion-free fallback (DESIGN principle 11's settled state). A GSAP tween is NOT covered
  // by the CSS @media (prefers-reduced-motion) collapse the way the old hint-pop keyframe was,
  // so the gate is the affordance's own responsibility now.
  if (!shouldAnimateReveal()) return;
  if (gsap.isTweening(cardEl)) return;
  gsap.fromTo(
    cardEl,
    { "--hint-ring": 0, "--hint-ping": 0 },
    {
      "--hint-ring": 1, // ring expands to its outer extent at the ping's peak
      "--hint-ping": 0.2, // play-test: a light brightness lift riding the ring; keep subtle
      duration: 0.5, // play-test: ring expansion time; affects how "snappy" the ping reads
      ease: "power3.out",
      yoyo: true,
      repeat: 1, // out then back in — one-shot, settles into the static highlight
      onComplete: () =>
        gsap.set(cardEl, { clearProps: "--hint-ring,--hint-ping" }),
    },
  );
}

function applyBidActionHint(appEl, hints) {
  const action = hints[0] === "accept" ? "accept" : "pass";
  floatIconTile(appEl, `.icon-tile[data-action="${action}"]`);
}

function applyHandDecisionHint(appEl, hints) {
  const action = hints[0] === "hand" ? "hand" : "skat";
  floatIconTile(appEl, `.icon-tile[data-action="${action}"]`);
}

function applyGameTypeHint(appEl, hints) {
  floatIconTile(appEl, `.icon-tile[data-type="${hints[0]}"]`);
}

function applyDiscardHint(appEl, hints, isFreshHint) {
  const selection = getDiscardSelection();
  hints.forEach((code) => {
    if (!selection.has(code)) floatHandCard(appEl, code, isFreshHint);
  });
}

function floatHandCard(appEl, code, isFreshHint) {
  const cardEl = appEl.querySelector(`.hand .card[data-code="${code}"]`);
  if (cardEl && !cardEl.classList.contains("hint-float")) {
    cardEl.classList.add("hint-float");
    if (isFreshHint) pingHintCard(cardEl);
  }
}

function floatIconTile(appEl, selector) {
  const tile = appEl.querySelector(selector);
  if (tile) tile.classList.add("hint-float");
}

function showReasonIfPresent(state) {
  // A bundle with no reason is the third removal seam (beside applyHint's empty bundle and
  // scheduleHint's no-delay returns): this prompt has no reason to show, so the float standing
  // there is the previous one's and must go, rather than being silently stranded.
  if (state.hintReasonKey == null) {
    removeHintReason();
    return;
  }
  showHintReason(
    state.hintReasonKey,
    state.hintReasonArgs || [],
    state.roundNumber,
    state.hintTopic,
    state.hintTopicAnchor,
  );
  // Stamped after the reconcile so it lands on whichever node is now standing — the kept one on
  // the same-content path, the rebuilt one otherwise. Read by removeReasonForOtherPrompt.
  const el = document.querySelector(".hint-reason");
  if (el != null) el.dataset.promptAction = state.pendingAction;
}

/** The float's content identity: round + reason key + reason args. Two consumers with two
 *  different jobs read the same string — the float node carries it as `data-reason-key` (is the
 *  float already showing this? then leave it alone) and `isReasonFresh` compares it against
 *  `appEl.dataset.lastReasonKey` (has the content changed since the last reveal? then fade). */
function reasonContentKey(reasonKey, reasonArgs, roundNumber) {
  return `${roundNumber}|${reasonKey}|${reasonArgs.join(",")}`;
}

function isReasonFresh(key) {
  const appEl = getApp();
  if (!appEl) return true;
  const fresh = appEl.dataset.lastReasonKey !== key;
  appEl.dataset.lastReasonKey = key;
  return fresh;
}

// --- Hint reason rendering ---

/** Card codes are 2-3 chars: rank + suit letter (e.g., "JH", "10S", "AC") */
function isCardCode(str) {
  return typeof str === "string" && /^(10|[7-9JQKA])[DHSC]$/.test(str);
}

/** Suit names as returned by GameTypeFormatter (e.g., "DIAMONDS", "HEARTS") */
const SUIT_NAME_MAP = {
  DIAMONDS: "diamonds",
  HEARTS: "hearts",
  SPADES: "spades",
  CLUBS: "clubs",
};

/** Any game type GameTypeFormatter can emit into a reason ARG. RAMSCH is deliberately absent and
 *  stays so: it isn't declarable, so chooseGameType never returns it and no reason arg carries it.
 *  The Ramsch mark does reach the float — the three `hintBidAvoidRamsch*` keys name it with a
 *  `{icon:fallen-chalice}` marker — but that is the copy's own vocabulary, not a wire value, so it
 *  never passes through here. */
function isGameTypeToken(str) {
  return (
    typeof str === "string" &&
    (str in SUIT_NAME_MAP || str === "GRAND" || str === "NULL")
  );
}

/** Parses a card code into { suit, rank } using lowercase names matching cards.js */
function parseCardCode(code) {
  const suitChar = code.charAt(code.length - 1);
  const rankStr = code.substring(0, code.length - 1);
  return { suit: SUIT_NAMES[suitChar], rank: RANK_NAMES[rankStr] };
}

/** Creates a small inline card reference: suit pip SVG + localized rank text */
function createInlineCardRef(cardCode) {
  const { suit, rank } = parseCardCode(cardCode);
  const color = suitColor(suit);

  const span = document.createElement("span");
  // `card-chip` is the vellum card behind the composite (skat.css § Glossary) — one material with
  // the learning surfaces' `{c:}` indicator, which renders the same pip-plus-letter pair.
  span.className = "hint-card-ref card-chip";

  span.appendChild(
    clearBakedVerticalAlign(createSuitPipSvg(suit, undefined, color), suit),
  );

  const rankEl = document.createElement("span");
  rankEl.className = "hint-card-rank";
  rankEl.textContent = rankDisplay(rank);
  rankEl.style.color = color;
  span.appendChild(rankEl);

  return span;
}

/** A concept icon named by a `{icon:<token>}` marker in the copy — the hint float's half of the
 *  token vocabulary `icons/icon-builders.js` owns. Wears `.hint-card-ref` for the sizing rule that
 *  governs every mark the float speaks, and `.hint-suit-only` like the game-type ref below to say
 *  no rank letter follows. An unknown token throws here rather than rendering an empty span — a copy
 *  typo is a bug, not a degraded render (CODING § let errors propagate); `icon-builders.test.js`'s
 *  bundle-wide guard is what keeps it from ever reaching a browser. */
function createInlineConceptIcon(token) {
  const span = document.createElement("span");
  // Only a suit pip takes the vellum chip, so a pip that arrives as copy and one that arrives as a
  // wire value (createInlineGameTypeRef below) render identically — the same uniformity argument
  // the `.hint-card-ref svg` rule makes for sizing. Every other concept mark stays bare.
  span.className = `hint-card-ref hint-suit-only${token.startsWith("pip-") ? " card-chip" : ""}`;
  span.appendChild(clearBakedVerticalAlign(ICON_BUILDERS[token]()));
  return span;
}

/** The contract's mark only, no rank — used for game type hints where the type alone is enough
 *  context: a suit pip for a Farbspiel, the chalice / oval zero for Grand / Null. Wears
 *  `.hint-suit-only` beside `.hint-card-ref` to say no rank letter follows. Size stays
 *  unset on both paths so the `.hint-card-ref svg` CSS rule owns the rendered inline size.
 *  A suit goes through createSuitPipSvg rather than createGameTypeIcon's own suit branch: that
 *  branch draws the trump-gradient game-type pip, a heavier mark than the flat suit-colored pip
 *  this float has always shown. */
function createInlineGameTypeRef(gameType) {
  const suit = SUIT_NAME_MAP[gameType];

  const span = document.createElement("span");
  // The chip is card material, so only the suit branch takes it: Grand's chalice and Null's zero
  // are not cards, and being light marks they never had the contrast problem it fixes.
  span.className = `hint-card-ref hint-suit-only${suit != null ? " card-chip" : ""}`;
  span.appendChild(
    clearBakedVerticalAlign(
      suit != null
        ? createSuitPipSvg(suit, undefined, suitColor(suit))
        : createGameTypeIcon(gameType),
      suit,
    ),
  );

  return span;
}

/** Strips the inline `vertical-align` the shared mark factories bake in for their UI-chrome
 *  callers (cards.js#createSuitPipSvg sets `middle`), so the `.hint-card-ref svg` rule can own the
 *  baseline here — an inline style outranks it, which is why that rule sat dead until this call.
 *  Applied to every mark all three inline refs mount, the marker-driven concept icons included, so
 *  one rule governs the whole float.
 *
 *  A suit pip also gets its suit stamped as `data-suit`, which no stylesheet keys on any more: it
 *  was the hook for the card ref's per-suit ♦ baseline correction, and that correction retired
 *  with the shared frame it corrected (ink-frame-inline-card-refs — every mark is framed to its
 *  own ink now, so there is no family strip left to be wrong for one suit). It stays as an honest
 *  DOM statement that this mark IS a suit, read by `hints.icon-markers.test.js` to tell a ♠
 *  game-type ref from a concept mark. */
function clearBakedVerticalAlign(mark, suit) {
  mark.style.verticalAlign = "";
  if (suit != null) mark.dataset.suit = suit;
  return mark;
}

/**
 * Reconciles the hint reason float against the fresh bundle — the float is a companion of the
 * prompt, so identical content keeps the node it is already showing on and only the position is
 * refreshed (mirroring renderPromptZone's `data-prompt-key` gate, js/ANIMATIONS § Between-renders
 * dataset gates), while changed content is rebuilt with the `.fresh` fade. Rebuilding identical
 * text on every resting commit is what made the float blink through a bid ladder.
 *
 * The float itself: the reason with its inline marks — card refs, game-type refs and `{icon:}`
 * concept icons (module header). When the bundle carries `hintTopic` (strategy-pages-slice1
 * design), it is a real `<button>` instead of a plain `<div>` — native keyboard focus/activation
 * for free, no hand-rolled ARIA — carrying the `.linked` modifier (the CSS override that makes it
 * clickable: the base `.hint-reason` rule is deliberately `pointer-events: none`, click-through so
 * the float never blocks the board). The plain, unmapped float is untouched.
 */
function showHintReason(
  reasonKey,
  reasonArgs,
  roundNumber,
  hintTopic,
  hintTopicAnchor,
) {
  const key = reasonContentKey(reasonKey, reasonArgs, roundNumber);
  const existing = document.querySelector(".hint-reason");
  if (existing != null && existing.dataset.reasonKey === key) {
    // Already showing exactly this: reposition only. No re-added `.fresh` (its fade would re-fire
    // on unchanged text) and no frameMarksToInk — this node's marks are already framed, the same
    // contract reflowHintReason states.
    positionHintReason(existing, getApp()?._layout?.hintZone);
    return;
  }
  removeHintReason();

  const content = resolveWithVisualSentinels(reasonKey, reasonArgs);
  if (content === reasonKey) return; // key not found — and the stale float is already gone

  const linked = hintTopic != null;
  const el = document.createElement(linked ? "button" : "div");
  // why: .fresh gates the fade-in keyframe so it fires only on content
  // change, not on every render-driven re-mount of this element.
  const fresh = isReasonFresh(key);
  el.className = `hint-reason${fresh ? " fresh" : ""}${linked ? " linked" : ""}`;
  // The content identity this float is showing, on the node — read by the same-content gate
  // above. Zone-scoped like `data-prompt-key` and for the same reasons: clearAppForRender sweeps
  // the node carrying it, so it self-resets and needs no resetFreshnessGates entry, and it is
  // deliberately not `last`-prefixed (it suppresses a rebuild, not an animation re-fire).
  el.dataset.reasonKey = key;

  appendHintContent(el, content);

  if (linked) linkFloatToTopic(el, hintTopic, hintTopicAnchor);

  attachHintToApp(el);
}

/** Turn the built float into the topic's doorway: a trailing decorative chevron glyph, and an
 *  activation that dynamic-imports reference-overlay.js (keeps its self-wired enhance modules
 *  inert on the game page, the established shape hint-button.js and game-menu.js already use) and
 *  opens the topic as a vellum overlay. */
function linkFloatToTopic(el, hintTopic, hintTopicAnchor) {
  el.type = "button";
  const glyph = document.createElement("span");
  glyph.className = "hint-reason-glyph";
  glyph.setAttribute("aria-hidden", "true"); // decorative — the button's own text is the label
  // The overlay shell's back chevron, mirrored: one mark, two directions
  // (icons/prompt-icons.js#createPateeChevron). Drawn since overlay-close-
  // heritage-x-icon, replacing a typographic `›`. The SVG goes INSIDE the
  // span rather than replacing it — the span is the wrap host the paragraph
  // below is about, it carries the aria-hidden and the --ui-warning colour
  // currentColor reads, and it keeps this element's `className` a plain
  // string (an SVGElement's is an SVGAnimatedString, which
  // hints.split.test.js's last-child assertions compare against).
  glyph.appendChild(createPateeChevron(null, "right"));
  // why: into the last half's span, not beside it. The halves are inline-blocks, so a
  // chevron appended as their sibling is its own wrap unit and drops to a line of its
  // own whenever the second half fills the float — measured at 352×638, that orphan
  // alone was the whole cost of the split: every trick-play string that reached four
  // lines (121.8px, past the 110.9px trick-slot band) came back to three (95.6px, the
  // pre-split tallest) with the chevron hidden. Inside the span it stays glued to the
  // last word and wraps with it. Document order is unchanged: still last, still after
  // both halves' text.
  const parts = el.querySelectorAll(":scope > .hint-reason-part");
  (parts[parts.length - 1] ?? el).appendChild(glyph);
  el.addEventListener("click", () => {
    import("./reference-overlay.js").then((m) =>
      m.openStrategyOverlay(hintTopic, hintTopicAnchor, "hint"),
    );
  });
}

/** The `{icon:<token>}` marker the copy carries — see the module header. Tokens are
 *  `ICON_BUILDERS` keys: lowercase, digits and hyphens. */
const ICON_MARKER = /\{icon:([a-z0-9-]+)\}/g;

/** Resolve the reason key into a string carrying a `\x00VIS:<kind>:<value>\x00` sentinel wherever
 *  a visual mark belongs, so the surrounding text can be walked and the marks spliced back in at
 *  their real positions. The sentinel carries its own kind and value: no side table, and a part
 *  the walk below matches needs nothing but itself to render.
 *
 *  Visual args become self-carrying sentinels before `t()` substitutes them. The subsequent
 *  marker pass expands `{icon:…}` in the resolved copy; correctness relies on HintService's closed
 *  reason-arg vocabulary never containing marker syntax. */
function resolveWithVisualSentinels(reasonKey, reasonArgs) {
  const placeholderArgs = reasonArgs.map((arg) =>
    isCardCode(arg)
      ? `\x00VIS:card:${arg}\x00`
      : isGameTypeToken(arg)
        ? `\x00VIS:gameType:${arg}\x00`
        : arg,
  );
  return t(reasonKey, ...placeholderArgs).replace(
    ICON_MARKER,
    (_, token) => `\x00VIS:icon:${token}\x00`,
  );
}

/** Append the resolved reason, split into its two halves at the punctuation the copy joins
 *  them with. Each half is its own inline-block span, so a wrap prefers that seam: both fit
 *  → one line; they don't → the second half drops to the next line whole, and the hint reads
 *  as the two parts it is. The single space between the spans stays a bare text node rather
 *  than padding on either — a trailing space inside an inline-block collapses, and that space
 *  IS the break opportunity the construct rides on. A one-part hint keeps today's shape
 *  exactly: parts straight onto `el`, no span. */
function appendHintContent(el, text) {
  const boundary = hintBoundaryIndex(text);
  if (boundary < 0) {
    appendHintParts(el, text);
    return;
  }
  appendHintHalf(el, text.slice(0, boundary));
  el.appendChild(document.createTextNode(" "));
  appendHintHalf(el, text.slice(boundary + 1));
}

/** Index of the space separating a hint's two halves. Every two-part hint string joins its
 *  halves with `. `, `: ` or `! ` (a situation or a full clause first) or else `, ` (an
 *  instruction, then its reason) — docs/design/COPY.md principle 6, the rule that retired the
 *  em-dash these strings used to carry. why the strong marks outrank the comma: the bidding
 *  strings carry a comma inside their own first half ("{5} trumps, worth {4}. Bid up to
 *  {6}, …") and must break at the period. Returns -1 for a one-part hint. Safe against the
 *  visual sentinels: each is one unbroken run with no space in it, and every pattern here is a
 *  mark FOLLOWED BY A SPACE — so a match can never land inside one, whatever punctuation the
 *  sentinel's own kind/value separators carry. */
function hintBoundaryIndex(text) {
  const strong = text.search(/[.:!] /);
  if (strong >= 0) return strong + 1;
  const comma = text.search(/, /);
  return comma >= 0 ? comma + 1 : -1;
}

/** One half of a split hint, in its own inline-block span so it wraps as a unit. */
function appendHintHalf(el, text) {
  const span = document.createElement("span");
  span.className = "hint-reason-part";
  appendHintParts(span, text);
  el.appendChild(span);
}

const NBSP = "\u00a0"; // escaped, not literal — an invisible source character is a trap
const VIS_PART = /^VIS:(card|gameType|icon):(.+)$/;

/** Walk a resolved string and append its parts to `host` in order — text nodes, and inline marks
 *  where a `VIS:<kind>:<value>` sentinel stands.
 *
 *  A non-breaking space touching a ref is handed to the ref rather than left in the text node
 *  beside it. The bid-hint copy glues its equation run with NBSPs so "× {suit} ({base}) = {value}"
 *  wraps as one unit, and that glue silently failed at the pip: Chrome treats an inline SVG as a
 *  contingent break and breaks between an NBSP and one, even though it honours the same NBSP
 *  between two glyphs (measured at 352×638 — "Spiel 4 ×" was left stranded on its own line).
 *  `.hint-card-ref` is `white-space: nowrap`, so an NBSP moved inside it stops being a break
 *  opportunity. A plain space is deliberately left where it is — that one IS the break
 *  opportunity the copy wants.
 *
 *  Scope, exactly: this glues an NBSP that sits between a ref and TEXT, which is every glued
 *  seam the ten bid-hint keys produce ({0} is always followed by "({1})", {7} by " im Skat").
 *  Two refs separated by nothing but an NBSP would NOT be fixed by it — the glue lands on the
 *  left ref's tail and the seam between the two spans is outside both nowraps, where Chrome
 *  breaks just the same. No key has that shape; a shared nowrap wrapper is what it would take,
 *  and inventing one for a string nobody writes is the abstraction CODING says to skip. */
function appendHintParts(host, text) {
  // why: null-byte sentinels can't appear in user strings, so they're a safe delimiter
  const parts = text.split(/\x00(VIS:[^\x00]+)\x00/);
  // Each text run decides which of its own edge NBSPs move out, leading edge first — so a run
  // that is nothing but one NBSP (two refs with only glue between them) hands it over once
  // rather than to both neighbours. The refs then read their neighbours' decisions rather than
  // testing the same characters a second time, which is where that double-move would come from.
  const moved = parts.map((part, i) => {
    if (VIS_PART.test(part)) return { lead: false, trail: false };
    const lead = VIS_PART.test(parts[i - 1] ?? "") && part.startsWith(NBSP);
    const rest = lead ? part.slice(1) : part;
    return {
      lead,
      trail: VIS_PART.test(parts[i + 1] ?? "") && rest.endsWith(NBSP),
    };
  });
  parts.forEach((part, i) => {
    const own = moved[i];
    appendHintPart(host, part, {
      lead: moved[i - 1]?.trail ?? false,
      trail: moved[i + 1]?.lead ?? false,
      text: part.slice(own.lead ? 1 : 0, own.trail ? -1 : undefined),
    });
  });
}

/** Append one tokenized part — a `VIS:<kind>:<value>` sentinel becomes the mark that kind names,
 *  anything else becomes a text node. `glue` carries the NBSP hand-off appendHintParts decided:
 *  `text` is the part with any handed-off NBSP already removed, and `lead`/`trail` are the NBSPs a
 *  ref has taken over from its neighbours.
 *
 *  The dispatch is exhaustive over VIS_PART's own alternation, which is the only thing that emits
 *  these sentinels — so the last arm is the icon case, not a fallback. A fourth kind widens
 *  VIS_PART and this switch; nothing else, since the sentinel carries its own value. */
function appendHintPart(host, part, glue) {
  const match = part.match(VIS_PART);
  if (!match) {
    if (glue.text) host.appendChild(document.createTextNode(glue.text));
    return;
  }
  const [, kind, value] = match;
  const ref =
    kind === "card"
      ? createInlineCardRef(value)
      : kind === "gameType"
        ? createInlineGameTypeRef(value)
        : createInlineConceptIcon(value);
  if (glue.lead) ref.prepend(document.createTextNode(NBSP));
  if (glue.trail) ref.appendChild(document.createTextNode(NBSP));
  host.appendChild(ref);
}

/** Frame every mark the float speaks to its own INK, so one CSS height lands the same amount of
 *  DRAWN mark for every icon family. The mechanism — and why per-family margin corrections could
 *  never converge — is `icons/ink-frame.js`; what is local to the float is the scope and the seam
 *  below.
 *
 *  Scoped to `.hint-card-ref` — the class all three kinds wear, so the whole float is one framing
 *  regime — plus the linked variant's own trailing chevron (`.hint-reason-glyph`), which is
 *  chrome rather than a mark the copy speaks but has exactly the same problem: its 48-unit family
 *  box is 1.7× its drawn height, so sizing the box to the ink the affordance needs would grow the
 *  line box and walk the float out of computeHintZone's height model (skat.css's rule there
 *  carries the measured numbers). Framed, the box IS the ink and one CSS height means what it
 *  says. The card refs' suit pips were carved out of it until ink-frame-inline-card-refs
 *  (2026-09-05), keeping one shared frame so the four pips held their relative ink sizes; inline
 *  in a sentence a pip stands beside its own rank letter rather than beside its three siblings,
 *  though, so that comparison never happens and equal ink height wins instead (Andre, off a
 *  rendered A/B — the same call the learning surfaces' card refs took).
 *
 *  Runs from attachHintToApp, between the append and positionHintReason: `getBBox` returns zeros
 *  off the DOM, and the float's height depends on the marks this resizes. reflowHintReason only
 *  re-positions an existing float, whose marks are already framed, so it does not run this.
 *
 *  Cost, measured in Chrome at 352×638 over 41 fresh renders (docs/PERFORMANCE.md governs this
 *  path): median 0.2 ms for a one-mark role hint, 0.4 ms for a two-mark one, worst sample 1.7 ms
 *  — negligible against the sync layout positionHintReason forces right after it, so no per-token
 *  ink cache. The stroke sweep, not `getBBox`, is where what little cost there is sits (0.1 ms of
 *  the two-mark median), which is what a cache would have to key on if this ever grows. */
function frameMarksToInk(el) {
  for (const mark of el.querySelectorAll(
    ".hint-card-ref svg, .hint-reason-glyph svg",
  ))
    frameToInk(mark);
}

/** Position `el` (already in the DOM) against the layout's hintZone — shared by
 *  attachHintToApp (build) and reflowHintReason (resize), the two seams that set
 *  the float's `top`. `hintZone.maxBottom` (trick phase only, layout-engine.js#
 *  computeLayout) is an engine-published CEILING, not a placement the engine can
 *  compute itself: it models a box and never measures glyphs, so only the
 *  renderer — reading `el`'s actual rendered height, in the DOM — can clamp
 *  correctly. `el.offsetHeight` forces a synchronous layout flush on every call:
 *  every `applyHint` that carries a `hintReasonKey` re-triggers `showHintReason`
 *  (rebuild, not just re-show — `applyHint` re-runs hint logic on every render,
 *  including a resize-driven one and the AWAIT_AI_CARD auto-play-commentary
 *  branch mid animation), not only on a genuinely new hint. Not measured against
 *  docs/PERFORMANCE.md's discipline; expected cheap (one small, off-by-default
 *  element) but unverified — flag if hint-adjacent jank is ever reported. On the
 *  attach path frameMarksToInk now runs immediately before this and calls
 *  `getBBox` per mark, so that flush is already spent by the time this reads
 *  `offsetHeight`; its own cost is measured, on frameMarksToInk. Absent
 *  `maxBottom` (every non-trick phase) leaves `top` at `hintZone.y`, exactly as
 *  before this ceiling existed. A box too tall for the band even at `top = 0` is
 *  left honest, un-papered — mirrors applyPromptContainerBound's own residual-
 *  overflow precedent (layout/CLAUDE.md § Load-bearing object mutation). */
function positionHintReason(el, hintZone) {
  if (!hintZone) return;
  const top =
    hintZone.maxBottom == null
      ? hintZone.y
      : Math.max(0, Math.min(hintZone.y, hintZone.maxBottom - el.offsetHeight));
  el.style.top = `${top}px`;
}

/** Attach the hint reason to the app, frame its concept marks to their ink, and position it
 *  against the layout's hintZone — in that order, and the order is the contract: both later
 *  steps measure, and the framing changes the height the positioning reads. */
function attachHintToApp(el) {
  const appEl = getApp();
  if (!appEl) return;
  appEl.appendChild(el); // append first — both steps below measure the DOM-attached element
  frameMarksToInk(el);
  positionHintReason(el, appEl._layout?.hintZone);
}

/** Take the float down. Exported for the one seam outside this module that must remove it without
 *  a state change to reconcile against: the mid-game hint-settings flip
 *  (game-start/game-start-profile-sections.js#reactToHintSettingChange), where hints going off
 *  must clear the reason text on the spot. Every in-game removal otherwise runs through the
 *  reconcile seams here. */
export function removeHintReason() {
  const existing = document.querySelector(".hint-reason");
  if (existing) existing.remove();
}

/** Resize reflow for a visible hint-reason float. attachHintToApp positioned it once
 *  at build; updateLayout() refreshes the hint-zone CSS vars but not that inline
 *  `top`, so a reason shown when the viewport changes would otherwise strand at the
 *  old vertical position. Re-run the same positioning against the fresh layout. */
export function reflowHintReason(app) {
  const el = app.querySelector(":scope > .hint-reason");
  if (!el) return;
  positionHintReason(el, app._layout?.hintZone);
}
